import { View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { fasUser } from 'shared/@generated/icons/fasUser';
import { Icon } from 'shared/components/icon';
import { Text } from 'shared/components/text';
import { ImageTile, TextLine } from '../illustration-interface';
import { illustrationPalette as palette, illustrationImagePalettes } from '../illustration-palette';
import type { IllustrationScale } from '../illustration-scene';

export function ProfileAvatar({ s, size }: { s: IllustrationScale; size: number }) {
  return (
    <View
      className="items-center justify-center rounded-full"
      style={{
        width: s(size),
        height: s(size),
        backgroundColor: palette.sageLight,
        borderWidth: s(1.2),
        borderColor: palette.paper,
      }}
    >
      <Icon
        icon={fasUser}
        className="text-brand-green-dark"
        style={{ width: s(size * 0.43), height: s(size * 0.43) }}
      />
    </View>
  );
}

export function ProfileName({ s, mobile = false }: { s: IllustrationScale; mobile?: boolean }) {
  const { t } = useTranslation('components');
  const prefix = mobile ? 'breeder-phone' : 'breeder-web';
  return (
    <View
      className={mobile ? 'items-center' : undefined}
      style={{ gap: s(0.5), width: mobile ? s(46) : undefined }}
    >
      <Text
        testID={prefix + '-name'}
        className="text-brand-brown-dark font-semibold"
        numberOfLines={1}
        style={{ fontSize: s(mobile ? 6 : 6.2), lineHeight: s(8) }}
      >
        {t('breeder_profile_illustration.sample_name')}
      </Text>
      <Text
        testID={prefix + '-breed'}
        className="text-brand-brown-dark/60"
        numberOfLines={1}
        style={{ fontSize: s(mobile ? 4.2 : 4.3), lineHeight: s(mobile ? 6 : 5.5) }}
      >
        {t('breeder_profile_illustration.sample_breed')}
      </Text>
    </View>
  );
}

export function ProfileAnimalCard({
  s,
  width,
  height,
  pet,
}: {
  s: IllustrationScale;
  width: number;
  height: number;
  pet: 'first' | 'second';
}) {
  const { t } = useTranslation('components');
  const photoWidth = width - 5.5;
  return (
    <View
      testID="breeder-animal-card"
      className="border-brand-brown-dark/25 items-start overflow-hidden"
      style={{
        width: s(width),
        height: s(height),
        padding: s(2),
        gap: s(1.5),
        borderWidth: s(0.75),
        borderRadius: s(4.5),
        backgroundColor: palette.paper,
      }}
    >
      <View testID={'animal-photo-' + pet}>
        <ImageTile
          s={s}
          width={photoWidth}
          height={height - 14}
          motif="mountain"
          colors={
            pet === 'first' ? illustrationImagePalettes.clay : illustrationImagePalettes.olive
          }
        />
      </View>
      <Text
        testID={'animal-name-' + pet}
        className="text-brand-brown-dark font-semibold"
        numberOfLines={1}
        style={{ width: s(photoWidth), fontSize: s(4.5), lineHeight: s(7) }}
      >
        {pet === 'first'
          ? t('breeder_profile_illustration.sample_pet')
          : t('breeder_profile_illustration.sample_second_pet')}
      </Text>
    </View>
  );
}

export function ProfilePhone({ s }: { s: IllustrationScale }) {
  return (
    <View testID="breeder-profile-phone" style={{ width: s(58), height: s(124) }}>
      <View
        className="bg-brand-brown-dark absolute rounded-full"
        style={{ left: s(-0.8), top: s(29), width: s(1.2), height: s(9) }}
      />
      <View
        className="bg-brand-brown-dark absolute rounded-full"
        style={{ right: s(-0.8), top: s(35), width: s(1.2), height: s(13) }}
      />
      <View
        className="border-brand-brown-dark absolute inset-0 overflow-hidden"
        style={{ backgroundColor: palette.paper, borderWidth: s(1.6), borderRadius: s(12) }}
      >
        <View className="items-center" style={{ height: s(8), paddingTop: s(2) }}>
          <View
            className="bg-brand-brown-dark rounded-full"
            style={{ width: s(19), height: s(3.5) }}
          />
        </View>
        <View className="items-center" style={{ paddingHorizontal: s(4.4) }}>
          <ImageTile
            s={s}
            width={46}
            height={24}
            motif="mountain"
            colors={illustrationImagePalettes.forest}
          />
          <View style={{ marginTop: s(-5.5) }}>
            <ProfileAvatar s={s} size={12} />
          </View>
          <View style={{ marginTop: s(2) }}>
            <ProfileName s={s} mobile />
          </View>
          <View style={{ gap: s(2.5), marginTop: s(6.5) }}>
            <TextLine s={s} width={42} height={1.5} className="bg-brand-brown-dark/25" />
            <TextLine s={s} width={34} height={1.5} className="bg-brand-brown-dark/15" />
          </View>
          <View className="flex-row" style={{ gap: s(3), marginTop: s(5) }}>
            <ProfileAnimalCard s={s} width={21.5} height={33} pet="first" />
            <ProfileAnimalCard s={s} width={21.5} height={33} pet="second" />
          </View>
        </View>
        <View
          className="bg-brand-brown-dark/35 absolute self-center rounded-full"
          style={{ bottom: s(3), width: s(19), height: s(1.5) }}
        />
      </View>
    </View>
  );
}
